<template>
  <nv-tab-page>
    <template #toolbar>
      <v-menu offset-y>
        <template #activator="{ on }">
          <v-slide-x-transition>
            <v-btn
              v-if="
                $auth.hasPermissions('owl.marketview') &&
                  classifiable &&
                  (ruleType === 'domain' || canView())
              "
              color="secondary"
              class="ml-n4"
              data-cy="add-domain-btn"
              v-on="on"
            >
              <v-icon class="mr-1">mdi-chevron-down</v-icon>
              Add
            </v-btn>
          </v-slide-x-transition>
        </template>
        <v-list data-cy="add-menu">
          <v-list-item
            v-if="ruleType === 'domain'"
            data-cy="add-single"
            @click="domainsForm.show = true"
          >
            <v-list-item-title>
              Single
            </v-list-item-title>
          </v-list-item>
          <v-list-item
            v-if="ruleType === 'domain' || canView()"
            data-cy="add-bulk"
            @click="bulkDomainsForm.show = true"
          >
            <v-list-item-title>
              Bulk
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-btn
        v-if="$auth.hasPermissions('ant.discovery')"
        :disabled="!selected.length"
        color="accent"
        data-cy="rules-transfer-btn"
        @click="openTransferForm()"
      >
        <v-icon class="mr-1">mdi-transfer</v-icon>
        <span>Transfer</span>
      </v-btn>
      <v-btn
        v-if="$auth.hasPermissions('ant.discovery')"
        :disabled="!selected.length"
        color="secondary"
        data-cy="rules-status-btn"
        @click="openStatusForm()"
      >
        <v-icon class="mr-1">mdi-pencil</v-icon>
        <span>Edit Status</span>
      </v-btn>
      <v-btn
        v-if="
          $auth.hasPermissions('ant.discovery') &&
            settings.localization !== '*' &&
            classifiable &&
            canViewImportRule()
        "
        color="accent"
        data-cy="rules-import-btn"
        @click="openImportForm"
      >
        <v-icon class="mr-1">mdi-import</v-icon>
        <span>Import</span>
      </v-btn>
      <v-spacer />
      <v-btn-toggle v-model="ruleType" mandatory data-cy="type-selector">
        <v-btn v-for="rule in ruleTypes" :key="rule.value" :value="rule.value" text small>
          <span class="hidden-md-and-down">{{ rule.text }}</span>
          <v-icon class="hidden-lg-and-up">
            {{ `mdi-alpha-${rule.text[0].toLowerCase()}` }}
          </v-icon>
        </v-btn>
      </v-btn-toggle>
      <nv-table-search v-model="search" sync class="ml-4 mr-n4" />
    </template>

    <template #filters>
      <nv-table-filters ref="applied-filters" v-model="tableFilters" no-clear>
        <filter-sheet ref="filter-sheet" :statuses="typeStatuses" @changed="setTableFilters" />
      </nv-table-filters>
    </template>

    <v-data-table
      v-model="selected"
      :headers="headers()"
      :items="items"
      :loading="loading"
      :options.sync="table"
      :server-items-length="totalItems"
      :footer-props="footerProps"
      :items-per-page.sync="table.itemsPerPage"
      show-select
    >
      <template #item.match_type="{ item }">
        {{ item.match_type | humanize }}
      </template>

      <template #item.ua="{ item }">
        <div class="d-flex justify-space-between">
          <v-btn v-if="ruleType === 'ios'" icon @click="openUAForm(item.id)">
            <v-icon>mdi-format-list-bulleted</v-icon>
          </v-btn>
        </div>
      </template>

      <template #item.pattern="{ item }">
        <div class="d-flex justify-space-between align-center">
          <router-link :to="{ name: `owl.${ruleType}.rules.show`, params: { ruleId: item.id } }">
            {{ item.pattern }}
          </router-link>
          <v-btn
            v-if="ruleType === 'domain'"
            :href="'https://' + item.pattern"
            icon
            target="_blank"
          >
            <v-icon>mdi-open-in-new</v-icon>
          </v-btn>
        </div>
      </template>

      <template #item.updated="{ item }">
        <updated-details :item="item" />
      </template>

      <template #item.status="{ item }">
        {{ item.status | humanize }}
      </template>
      <template #item.property="{ item }">
        {{ item.property | humanize }}
      </template>
      <template #item.country="{ item }">
        <CountryBadge :country="resolveCountryCode(item.localization)" />
      </template>
    </v-data-table>
    <domain-form v-model="domainsForm.show" :tree-id="id" />
    <bulk-domain-form v-if="!canView()" v-model="bulkDomainsForm.show" :tree-id="id" />
    <bulk-csv-upload-form
      v-else
      v-model="bulkDomainsForm.show"
      :tree-id="id"
      :rule-type="ruleType"
      :localization="settings.localization"
    />
    <transfer-patterns-form
      v-model="transferForm.show"
      :kind="ruleType"
      :selected-ids="selectedIds"
      :child-id="id"
      @saved="saved"
    />
    <rules-status-form
      v-model="statusForm.show"
      :rule-type="ruleType"
      :editing="statusForm.edit"
      :statuses="availableStatuses"
      @saved="saved"
    />
    <ua-dialog v-model="uaDialog.show" :match-id="uaDialog.matchId" />
    <v-dialog v-model="importForm.show" persistent max-width="600px">
      <nv-form
        :title="'Import Rules'"
        data-cy="import-rules-form"
        @cancel="importForm.show = false"
        @nv-submit="submitImport"
      >
        <template #submit-button>
          <v-btn color="primary" @click="submitImport">Import</v-btn>
        </template>
        <v-row>
          <v-col cols="12">
            <label class="text-subtitle2 font-weight-medium mb-2 d-block"
              >Select Source Tree-ID</label
            >
            <tree-search
              v-if="importForm.show"
              v-model="importForm.sourceTreeId"
              mode="classification"
              country-code="*"
            />
          </v-col>
        </v-row>
        <v-row class="mt-6">
          <v-col cols="12">
            <label class="text-subtitle2 font-weight-medium mb-3 d-block">Select Rule Types</label>
            <v-row>
              <v-col cols="4">
                <v-checkbox v-model="importForm.ruleTypes" label="Domain" value="Domain" />
              </v-col>
              <v-col cols="4">
                <v-checkbox v-model="importForm.ruleTypes" label="OTTM" value="OTTM" />
              </v-col>
              <v-col cols="4">
                <v-checkbox v-model="importForm.ruleTypes" label="Video" value="Video" />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row v-if="importForm.ruleTypes.length" class="mt-4">
          <v-col cols="12">
            <v-card class="pa-3" outlined>
              <p class="mb-0 text-body2">
                <strong>{{
                  importForm.ruleTypes
                    .map(t => t.charAt(0).toUpperCase() + t.slice(1).toLowerCase())
                    .join(', ')
                }}</strong>
                rules of the this tree-id will be imported.
              </p>
            </v-card>
          </v-col>
        </v-row>
      </nv-form>
    </v-dialog>
  </nv-tab-page>
</template>

<script>
import HTTP from '@/api/ant';
import { TableMixin, enumFetch } from '@nielsen-media/ui-toolkit-vue-api';
import FilterSheet from '@/components/mv/PatternsFilterSheet';
import DomainForm from '@/components/mv/DomainForm';
import TransferPatternsForm from '@/components/mv/TransferPatternsForm';
import UaDialog from '@/components/cls/ios/UaDialog';
import RulesStatusForm from '@/components/cls/RulesStatusForm';
import BulkCsvUploadForm from '@/components/mv/BulkCSVUploadForm.vue';
import BulkDomainForm from '@/components/mv/BulkDomainForm.vue';
import UpdatedDetails from '@/components/cls/UpdatedDetails.vue';
import { resolveCountryCode, getConfigMap } from '@/helpers/filters';
import CountryBadge from '@/components/CountryBadge';
import TreeSearch from '@/components/mv/TreeSearch';

const defaultHeaders = [
  { text: 'ID', sortable: true, value: 'id' },
  { text: 'Pattern', sortable: false, value: 'pattern' },
  { text: 'Status', sortable: false, value: 'status' },
  { text: 'Updated', sortable: true, value: 'updated' },
  { text: 'Market', sortable: false, value: 'country' },
];
const ottmHeaders = [
  { text: 'ID', sortable: true, value: 'id' },
  { text: 'Pattern', sortable: false, value: 'pattern' },
  { text: 'Property', sortable: false, value: 'property' },
  { text: 'Status', sortable: false, value: 'status' },
  { text: 'Updated', sortable: true, value: 'updated' },
  { text: 'Market', sortable: false, value: 'country' },
];
const iosHeaders = [
  { text: 'ID', sortable: true, value: 'id' },
  { text: 'Pattern', sortable: false, value: 'pattern' },
  { text: 'Match Type', sortable: false, value: 'match_type' },
  { text: 'User Agents', sortable: false, value: 'ua' },
  { text: 'Domain', sortable: false, value: 'domain' },
  { text: 'Status', sortable: false, value: 'status' },
  { text: 'Updated', sortable: true, value: 'updated' },
  { text: 'Market', sortable: false, value: 'country' },
];
const types = {
  tree: [
    { text: 'Domain', value: 'domain' },
    { text: 'Connected Devices', value: 'ottm' },
    { text: 'Video', value: 'video' },
  ],
  app_dictionary: [
    { text: 'Android', value: 'android' },
    { text: 'IOS', value: 'ios' },
  ],
};
const kinds = ['tree', 'app_dictionary'];

export default {
  components: {
    UpdatedDetails,
    BulkCsvUploadForm,
    BulkDomainForm,
    UaDialog,
    RulesStatusForm,
    TransferPatternsForm,
    DomainForm,
    FilterSheet,
    CountryBadge,
    TreeSearch,
  },
  mixins: [TableMixin],
  props: {
    id: { type: Number, required: true },
    kind: {
      type: String,
      required: true,
      validator: v => kinds.indexOf(v) !== -1,
    },
    classifiable: {
      type: Boolean,
      default: false,
    },
    countryCode: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      settings: {
        [`${this.kind}_id`]: this.id,
        localization: this.countryCode === 'Default' ? '*' : this.countryCode,
      },
      ruleTypes: types[this.kind],
      ruleType: this.kind === 'tree' ? 'domain' : 'android',
      tableParam: 'patterns.dt',
      domainsForm: { show: false },
      bulkDomainsForm: { show: false },
      selected: [],
      transferForm: {
        show: false,
        selectedIds: [],
      },
      uaDialog: {
        show: false,
        matchId: 0,
      },
      statusForm: {
        show: false,
        edit: null,
      },
      importForm: {
        show: false,
        sourceTreeId: null,
        ruleTypes: [],
      },
      domainStatuses: [],
      ottmStatuses: [],
      videoStatuses: [],
      androidStatuses: [],
      iosStatuses: [],
    };
  },
  computed: {
    selectedIds() {
      return this.selected.map(el => el.id);
    },
    availableStatuses() {
      return this.typeStatuses.filter(s => s.value !== this.tableFilters.status);
    },
    typeStatuses() {
      return this[`${this.ruleType}Statuses`];
    },
  },
  watch: {
    ruleType() {
      this.selected = [];
      this.items = [];
      if (this.search) {
        this.search = '';
      } else {
        this.tableFetch();
      }
    },
  },
  created() {
    this.enumFetch(HTTP, {
      domainStatuses: '/domain/rules/enum/statuses',
      ottmStatuses: '/ottm/rules/enum/statuses',
      videoStatuses: '/video/rules/enum/statuses',
      androidStatuses: '/android/rules/enum/statuses',
      iosStatuses: '/ios/rules/enum/statuses',
    });
  },
  methods: {
    canView() {
      const config = this.getConfigMap();
      return config['bulkUploadFeature_owl_ui']?.enabled || false;
    },
    canViewImportRule() {
      const config = this.getConfigMap();
      return config['imporRulesFeature_owl_ui']?.enabled || false;
    },
    tableFetch() {
      this.paginateTableData(HTTP, `/${this.ruleType}/rules`);
    },
    openTransferForm() {
      this.transferForm.show = true;
    },
    saved() {
      this.selected = [];
      this.tableFetch();
    },
    openStatusForm() {
      this.statusForm.edit = {
        ids: this.selectedIds,
        status: null,
      };
      this.statusForm.show = true;
    },
    openUAForm(id) {
      this.uaDialog.matchId = id;
      this.uaDialog.show = true;
    },
    openImportForm() {
      this.importForm.show = true;
    },
    async submitImport() {
      if (!this.importForm.sourceTreeId || !this.importForm.ruleTypes.length) {
        this.$flash('error', 'Please select source tree and at least one rule type');
        return;
      }
      try {
        await HTTP.post('/localized_rule_import', {
          source_tree_id: this.importForm.sourceTreeId,
          target_tree_id: this.settings.tree_id,
          target_localization: this.settings.localization,
          rule_types: this.importForm.ruleTypes,
        });
        this.$flash('success', 'Rules imported successfully');
        this.importForm = {
          show: false,
          sourceTreeId: null,
          ruleTypes: [],
        };
        this.tableFetch();
      } catch (error) {
        this.$flash('error', 'Failed to import rules');
      }
    },
    headers() {
      if (this.ruleType === 'ios') {
        return iosHeaders;
      }
      if (this.ruleType === 'ottm') {
        return ottmHeaders;
      }
      return defaultHeaders;
    },
    enumFetch,
    resolveCountryCode,
    getConfigMap,
  },
};
</script>
